// El autolinker de gtag solo decora clicks en <a> y submits de <form>, no navegaciones con
// window.location.href: decorateWithGaLinker le pide a gtag el `_gl` con un <a> temporal y, si gtag no
// decora, lo arma con el mismo algoritmo de gtag.js (solo GA4, sin las cookies de Google Ads)
const CLIENT_COOKIE_FORMAT = /^GA\d\.\d+\.(.+)$/;
const SESSION_COOKIE_FORMAT = /^GS\d\.\d+\.(.+)$/;
let crcTable = null;
function getCrcTable() {
    if (!crcTable) {
        crcTable = [];
        for (let n = 0; n < 256; n++) {
            let c = n;
            for (let k = 0; k < 8; k++) {
                c = c & 1 ? (c >>> 1) ^ 0xedb88320 : c >>> 1;
            }
            crcTable[n] = c;
        }
    }
    return crcTable;
}
// base64 con el alfabeto de gtag: `-_` en lugar de `+/` y `.` como padding
function encodeValue(value) {
    return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '.');
}
// gtag valida el hash con el fingerprint del navegador y el minuto actual (acepta hasta 2 minutos atrás)
function getFingerprintHash(data) {
    const nav = navigator;
    const fingerprint = [
        nav.userAgent,
        new Date().getTimezoneOffset(),
        nav.userLanguage || nav.language,
        Math.floor(Date.now() / 60000),
        data,
    ].join('*');
    const table = getCrcTable();
    let crc = -1;
    for (let i = 0; i < fingerprint.length; i++) {
        crc = (crc >>> 8) ^ table[(crc ^ fingerprint.charCodeAt(i)) & 255];
    }
    return ((crc ^ -1) >>> 0).toString(36);
}
function readCookies() {
    return document.cookie
        .split(';')
        .map((cookie) => {
        const index = cookie.indexOf('=');
        return [cookie.slice(0, index).trim(), cookie.slice(index + 1).trim()];
    })
        .filter(([name]) => name);
}
/**
 * Genera el parámetro `_gl` con las cookies de GA4 del sitio actual para que el booking engine
 * conserve el mismo usuario y sesión de Google Analytics
 * @param onUnknownFormat - se llama por cada cookie de GA con un formato que no se reconoce (se omite del `_gl`)
 * @returns el valor de `_gl`, o null si no hay cookies de GA (sin GA, sin consentimiento, etc.)
 */
export function getGaLinkerParam(onUnknownFormat) {
    try {
        const clientValues = [];
        const sessionValues = [];
        readCookies().forEach(([name, value]) => {
            // `deleted` es el valor con el que scripts de terceros "borran" la cookie; no es un formato nuevo de GA
            if ((name !== '_ga' && !name.startsWith('_ga_')) || !value || value === 'deleted') {
                return;
            }
            const isClient = name === '_ga';
            const match = value.match(isClient ? CLIENT_COOKIE_FORMAT : SESSION_COOKIE_FORMAT);
            if (!match) {
                onUnknownFormat === null || onUnknownFormat === void 0 ? void 0 : onUnknownFormat(name, value);
                return;
            }
            if (isClient) {
                clientValues.push([name, match[1]]);
            }
            else {
                sessionValues.push([name, match[1]]);
            }
        });
        // gtag pone primero el client id y después las sesiones de cada stream
        const values = [...clientValues, ...sessionValues];
        if (!values.length) {
            return null;
        }
        const data = values.map(([name, value]) => `${name}*${encodeValue(value)}`).join('*');
        return ['1', getFingerprintHash(data), data].join('*');
    }
    catch (_a) {
        return null;
    }
}
const GL_PARAM = /[?&#]_gl=/;
function removeGlParam(url) {
    return url.replace(/([?&])_gl=[^&#]*&?/, '$1').replace(/[?&](#|$)/, '$1');
}
// el `_gl` va con `*` literales, como lo pone gtag (con qs.stringify quedaban como %2A)
function appendGlParam(url, gl) {
    const hashIndex = url.indexOf('#');
    const base = hashIndex < 0 ? url : url.slice(0, hashIndex);
    const hash = hashIndex < 0 ? '' : url.slice(hashIndex);
    return `${base}${base.includes('?') ? '&' : '?'}_gl=${gl}${hash}`;
}
// gtag decora el <a> en mousedown con su propio `_gl` (incluye `_gcl_au` / `_gcl_aw` de Google Ads),
// solo si el dominio destino está en linker.domains; mousedown y no click para que GTM no lo cuente como clic
function decorateWithGtag(url) {
    try {
        const link = document.createElement('a');
        link.href = url;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
        link.remove();
        return GL_PARAM.test(link.href) ? link.href : null;
    }
    catch (_a) {
        return null;
    }
}
/**
 * Agrega a la url el `_gl` (cross-domain de GA4 y Google Ads) para que el destino conserve el mismo usuario y sesión.
 * Reemplaza el `_gl` que la url ya traiga (p. ej. reenviado desde la url actual, que pudo haber expirado)
 * @param onUnknownFormat - ver getGaLinkerParam; solo aplica cuando gtag no decora la url
 * @returns la url decorada, o la original si no hay con qué armar el `_gl`
 */
export function decorateWithGaLinker(url, onUnknownFormat) {
    const cleanUrl = removeGlParam(url);
    const decorated = decorateWithGtag(cleanUrl);
    if (decorated) {
        return decorated;
    }
    const gl = getGaLinkerParam(onUnknownFormat);
    return gl ? appendGlParam(cleanUrl, gl) : url;
}
